import React, { useEffect, useRef, useState } from 'react';
import '../components/CorrectorNotes.scss';
import { Formik, Form, FormikHelpers as FormikActions, getIn } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { isEmpty, cloneDeep } from 'lodash'

import { APIError, handle_drf_form_error } from 'django-rest-react';
import { FormGroupBootstrap, Anteprima, AnteprimaLaTeX, LoadingCircle, CategoriaSelector, MinimalLatexEditor } from '../components';
import {
  post_new_problema, get_problema_thread,
  patch_new_problema,
  crea_soluzione, modifica_soluzione, delete_soluzione,
  create_bozza, lint_text, build_tex_problema_post, build_tex_soluzione_post
} from '../api';
import { sentry_log, } from '../utils';

import type {
  Linter,
  BozzaProblemaData, ThreadLista,
} from '../api/types';
import { SOLUZIONE_KEYS, ThreadDettaglio } from '../api/types'
import {
  ErrorBlockGeneric, AllegatoMap,
  TextHelper, MultipleSoluzioniForm, ConfirmedButton,
  RisposteMultipleForm, CategoriaEventoSelector,
  CompetenzaSelector
} from '../components';
import { FONTE, login_url } from '../globals'
import { useAllegato, useCategoria, useCategoriaEvento, useCompetenza, useEvento, useLogin } from '../reducers';
import { PROBLEMA_TIPOLOGIA_RISPOSTA } from '../globals/auto-enums';
import { ATTR } from '../components/MultipleSoluzioniForm';

type PatchSoluzioneData = Parameters<typeof modifica_soluzione>[1]
type FormValues = Parameters<typeof post_new_problema>[0]
type SoluzioneData = Parameters<typeof crea_soluzione>[0]

enum LintedField {
  Testo = "problema.versioni[0].testo",
  Soluzione = "problema.soluzione_spiegata",
  Commenti = "problema.commenti_correttori"
}

type Linted = {
  [key in LintedField]: Linter[]
}

const empty_initial_values: FormValues = {
  titolo: '',
  problema: {
    titolo: '',
    tipologia_risposta: "num",
    allegati: [], categoria: [],
    eventi_proposto: [], soluzioni: [],
    soluzioni_scelta_multipla: [],
    fonte_verboso: "",
    soluzione_spiegata: "",
    commenti_correttori: "",
    fonte: "altro",
    versioni: [{
      testo: '',
    }]
  }
}


enum Button {
  Bozza,
  Invia,
}


const handleSaveDraftFactory = (navigate: (v: string) => void, setError: (v: string) => void) =>
  async (values: FormValues, actions: FormikActions<FormValues>) => {
    const sub_data = cloneDeep(values.problema) as unknown as BozzaProblemaData
    sub_data.testo = values.problema.versioni[0].testo
    // @ts-ignore
    delete sub_data.versioni
    const extraErrParse = (err: any) => {
      const errcp = cloneDeep(err)
      if (errcp.testo) {
        errcp.versioni = [{ testo: errcp.testo }]
      }
      return {
        problema: errcp
      }
    }

    return create_bozza(sub_data as BozzaProblemaData)
      .then(ans => navigate(`/drafts/${ans.id}/`))
      .catch((error: APIError) => handle_drf_form_error(error, actions, setError, extraErrParse))
      .finally(() => actions.setSubmitting(false))
  }


type Thread = Awaited<ReturnType<typeof get_problema_thread>>

const handleSoluzioniDifference = async (attr: ATTR, values: FormValues, initial: any, thread: Thread, errparse: ExtraErrParser) => {
  const soluzioni_iniziali = initial.problema[attr] as ThreadLista["problema"][ATTR];
  if (values.problema[attr] != soluzioni_iniziali) {
    const soluzioni = values.problema[attr] as NonNullable<typeof values.problema[ATTR]>
    for (let j = 0; j < soluzioni.length; j++) {
      const ogg = soluzioni[j] as ThreadLista["problema"][ATTR][0];
      if (ogg.id > 0) {
        // Qui devo capire che dati metterci
        let submitting_data_patch: PatchSoluzioneData = {};
        const old = soluzioni_iniziali.find(item => item.id == ogg.id);
        SOLUZIONE_KEYS.forEach(value => {
          // @ts-ignore
          if (old[value] != ogg[value]) submitting_data_patch[value] = ogg[value]
        })
        if (isEmpty(submitting_data_patch)) continue
        await modifica_soluzione(ogg.id, submitting_data_patch)
          .then(() => console.log("Modificato", ogg.id))
          .catch((error: APIError) => errparse(error, j, attr));
      } else {
        let subdata = { problema: thread.problema.id } as SoluzioneData
        // @ts-ignore
        SOLUZIONE_KEYS.forEach(value => subdata[value] = ogg[value])
        await crea_soluzione(subdata)
          .then(sol => console.log("Creata", sol))
          .catch((error: APIError) => errparse(error, j, attr))
      }
    }
    const rimosse = soluzioni_iniziali.filter(item =>
      !(values.problema as ThreadLista["problema"])[attr].map(i => i.id).includes(item.id));
    for (const ogg of rimosse) {
      await delete_soluzione(ogg.id)
        .then(() => console.log("Cancellato", ogg.id))
        .catch((error: APIError) => errparse(error, -1, attr))
    }
  }

}

type ExtraErrParser = (error: APIError, idx: number, attr: ATTR) => void;

const handleSubmitFactory = (
  navigate: (s: string) => void, setError: (s: string) => void,
  modifica: boolean,
  thread: Thread,
  initial: any,
  pk: number) => async (values: FormValues, actions: FormikActions<FormValues>) => {
    let submitting_data = cloneDeep(values);
    let error_happened = false;
    const sol_errors = {
      problema: {
        soluzioni: new Array(values.problema.soluzioni?.length),
        soluzioni_scelta_multipla: new Array(values.problema.soluzioni_scelta_multipla?.length),
      }
    }
    const non_field_errors = new Array(values.problema.soluzioni?.length || 0 + (values.problema.soluzioni_scelta_multipla?.length || 0))
    const errparse: ExtraErrParser = (error, idx, attr) => {
      error_happened = true;
      if (idx == -1) {
        non_field_errors.push("")
        idx = non_field_errors.length - 1
      }
      handle_drf_form_error(error, actions,
        s => non_field_errors[idx] = s,
        // @ts-ignore
        s => sol_errors.problema[attr][idx] = s)
    }
    if (modifica) {
      // Se è una modifica bisogna fare le seguenti cose:
      // - Controllare se il testo non è cambiato. Se non è cambiato bisogna
      //   cancellare il campo versioni da submitting_data
      // - Fare un confronto ultracancro delle Soluzioni precedenti e di quelle attuali
      //   per ciascuna si guarda inannzitutto se c'è l'id associato, se non c'è vuol dire
      //   che è nuova e sicuramente va sottomessa.
      //   Se c'è bisogna trovare quella vecchia corrispondente, vedere se è cambiata
      //   e in caso fare la patch giusta.

      const { problema } = thread;

      // Confrontiamo il testo
      const last = problema.versioni[problema.versioni.length - 1];
      if (last.testo == values.problema.versioni[0].testo) {
        submitting_data.problema.versioni = []
      };

      if (thread.problema.tipologia_risposta === "num") {
        await handleSoluzioniDifference("soluzioni", values, initial, thread, errparse);
        // @ts-ignore
        delete submitting_data.problema.soluzioni
      } else if (thread.problema.tipologia_risposta === "mul") {
        await handleSoluzioniDifference("soluzioni_scelta_multipla", values, initial, thread, errparse);
        // @ts-ignore
        delete submitting_data.problema.soluzioni_scelta_multipla
      }
    }

    if (error_happened) {
      // @ts-ignore
      actions.setErrors(cloneDeep(sol_errors))
      setError("Errore nella modifica delle domande: guarda sopra e qui. È caldamente consigliato salvarti in parte le modifiche fatte al testo, refreshare la pagina e riprovare. Tieni gli errori non riferiti a un singolo campo: " + JSON.stringify(non_field_errors))
      // Volontariamente non resettiamo il setsubmitting per impedire cose insensate
      // sottomettendo due volte
      return
    }

    const call_function = modifica ?
      () => patch_new_problema(pk, submitting_data) :
      () => post_new_problema(submitting_data);
    return call_function()
      .then(risposta => navigate(`/thread/problema/${risposta.id}/`))
      .catch((error: APIError) => handle_drf_form_error(error, actions, setError))
      .finally(() => actions.setSubmitting(false))
  }




const FormSection = ({ number, title, description, children }: {
  number: number;
  title: string;
  description: string;
  children: React.ReactNode;
}) => (
  <section className="card shadow-sm mb-4" aria-labelledby={`problema-section-${number}`}>
    <div className="card-header bg-light d-flex align-items-start">
      <span className="badge badge-primary rounded-pill mr-3 mt-1" aria-hidden="true">{number}</span>
      <div>
        <h2 className="h5 mb-1" id={`problema-section-${number}`}>{title}</h2>
        <p className="text-muted mb-0">{description}</p>
      </div>
    </div>
    <div className="card-body">{children}</div>
  </section>
);


const NuovoThreadProblema = (props: {}) => {

  const login = useLogin();
  const [thread, setThread] = useState<ThreadDettaglio | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [initial, setInitial] = useState(empty_initial_values);
  const [lints, setLints] = useState({
    [LintedField.Testo]: [],
    [LintedField.Soluzione]: [],
    [LintedField.Commenti]: [],
  } as Linted);

  const categorieEvento = useCategoriaEvento(props);
  const categorie = useCategoria(props);
  const competenze = useCompetenza(props);
  const eventi = useEvento(props);
  const allegati = useAllegato(props);
  const subButton = useRef(Button.Invia);
  const testoRef = useRef(null);
  const lastLint = useRef(new Date());
  const params = useParams<{ pk?: string }>();
  const navigate = useNavigate();

  if (!login.logged_in) {
    login_url()
  }

  const modifica = !!params.pk;
  const pk = modifica ? parseInt(params.pk) : null;

  useEffect(() => {
    if (modifica) {
      document.title = "Modifica problema - Owlitrack";
      get_problema_thread(pk as number).then(thread => {
        const { problema } = thread;
        const last = problema.versioni[problema.versioni.length - 1];
        const initial = {
          titolo: thread.titolo,
          problema: cloneDeep(problema)
        }
        initial.problema.versioni = [last]
        setThread(thread)
        setInitial({
          ...initial,
          problema: {
            ...initial.problema,
            fonte_verboso: initial.problema.fonte_verboso || undefined,
            commenti_correttori: initial.problema.commenti_correttori || undefined,
            soluzione_spiegata: initial.problema.soluzione_spiegata || undefined,
          }
        })
      })
    } else {
      document.title = "Nuovo problema - Owlitrack";
    }
  }, [modifica, pk])


  if (modifica && !thread) {
    return <LoadingCircle />;
  }

  // Manage errors
  let may_i_hide_json = true
  if (error) {
    if (Object.keys(error).filter(function (string) {
      return string != "titolo" && string != "testo" && string != "versioni" && string != "soluzioni"
    }).length > 0) {
      may_i_hide_json = false;
      // Se error contiene solo i campi già gestiti
      // dal form stesso, non mostrarlo nel blocco errore
    }
  }

  const handleSaveDraft = handleSaveDraftFactory(navigate, setError);
  const handleSubmit = handleSubmitFactory(
    navigate, setError, modifica,
    // @ts-ignore
    initial,
    thread, pk
  );

  const lint = (
    fieldname: LintedField,
    values: FormValues,
    setFieldValue: FormikActions<FormValues>["setFieldValue"],
    newval: string
  ) => {
    setFieldValue(fieldname, newval)
    if (new Date().getTime() - lastLint.current.getTime() > 1000 * 10 && !!newval && newval != "") {
      lastLint.current = new Date();
      lint_text(newval).then(linted => setLints(old => {
        return { ...old, [fieldname]: linted }
      }))
    }
  }

  return (
    <div className="container-fluid mb-4">
      <h1>{modifica ? "Modifica thread" : "Nuovo thread"}</h1>
      <p className="text-muted mb-4">Un unico modulo in cinque sezioni: dai titoli alle note per i correttori.</p>
      <Formik
        initialValues={initial} onSubmit={
          (values: FormValues, actions: FormikActions<FormValues>) => {
            let cusumano;
            switch (subButton.current) {
              case Button.Bozza: cusumano = handleSaveDraft(values, actions); break;
              case Button.Invia: cusumano = handleSubmit(values, actions); break;
              default: {
                sentry_log(new Error("Chiamato NuovoThreadProblema::handleSubmission con" +
                  ` parametro sbagliato: ${subButton}`));
                return;
              }
            }
            cusumano.finally(() => actions.setSubmitting(false));
          }
        }
        enableReinitialize={true}
      >
        {({ values, touched, errors, isSubmitting, setFieldValue, setFieldTouched, handleSubmit, handleReset }) => {
          const api_problema = !!thread?.id ? () => build_tex_problema_post(thread?.id, { override: values.problema.versioni[0].testo }) : undefined;
          const api_soluzione = !!thread?.id ? () => build_tex_soluzione_post(thread?.id, { override: values.problema.soluzione_spiegata }) : undefined;
          return (
            <>
              <Form className="form needs-validation">
                <FormSection
                  number={1} title="Titoli e tipologia"
                  description="Dai un nome al problema e scegli il tipo di risposta."
                >
                  <FormGroupBootstrap
                    name="titolo" type="text" displayName="Titolo" readOnly={modifica}
                  />
                  <FormGroupBootstrap
                    name="problema.titolo" type="text" displayName="Titolo serio"
                    help_text="Può essere uguale al titolo. Mettine uno diverso solo se il titolo che hai proposto non è adatto ad essere inserito in una gara ufficiale, per esempio se è volgare o è un meme che richiede troppa lore."
                  />
                  <FormGroupBootstrap
                    name="problema.tipologia_risposta" type="select"
                    help_text={
                      "Il tipo di risposta ad un problema influenzerà anche a che tipo di gare può essere assegnato un problema. " +
                      "Risposta numerica è il tipico problema da gara a squadre, risposta a scelta multipla è " +
                      "utile solo per la gara di primo livello, mentre risposta aperta è per i problemi che vanno corretti manualmente," +
                      " come Senigallia o il test di ammissione allo stage."
                    }
                    choices={PROBLEMA_TIPOLOGIA_RISPOSTA}
                  />
                </FormSection>
                <FormSection
                  number={2} title="Testo del problema"
                  description="Scrivi il testo, controlla l’anteprima e aggiungi eventuali allegati."
                >
                  <AllegatoMap
                    allegati={values.problema.allegati}
                    pubblicaAllegato={{ allegatiStr: "problema.allegati", setAllegati: val => setFieldValue("problema.allegati", val) }}
                  />
                  <MinimalLatexEditor
                    name={LintedField.Testo}
                    attachments={values.problema.allegati.map(id => ({ id, titolo: allegati?.find(attachment => attachment.id === id)?.titolo }))}
                    livePreview={<AnteprimaLaTeX fieldname={LintedField.Testo} lints={lints[LintedField.Testo]} mode="live" />}
                    displayName="Testo"
                    value={getIn(values, LintedField.Testo) || ''}
                    touched={!!getIn(touched, LintedField.Testo)}
                    error={getIn(errors, LintedField.Testo)}
                    onChange={(value) => lint(LintedField.Testo, values, setFieldValue, value)}
                    onBlur={() => setFieldTouched(LintedField.Testo, true, true)}
                  />
                  {!!getIn(values, LintedField.Testo) &&
                    <AnteprimaLaTeX fieldname={LintedField.Testo} latex_api={api_problema} mode="compiled" />
                  }
                </FormSection>
                <FormSection
                  number={3} title="Soluzione e risposte"
                  description="Spiega il procedimento, poi aggiungi le risposte richieste."
                >
                  <AllegatoMap
                    allegati={values.problema.allegati}
                    pubblicaAllegato={{ allegatiStr: "problema.allegati", setAllegati: val => setFieldValue("problema.allegati", val) }}
                  />
                  <MinimalLatexEditor
                    name={LintedField.Soluzione}
                    attachments={values.problema.allegati.map(id => ({ id, titolo: allegati?.find(attachment => attachment.id === id)?.titolo }))}
                    livePreview={<AnteprimaLaTeX fieldname={LintedField.Soluzione} lints={lints[LintedField.Soluzione]} mode="live" />}
                    displayName="Soluzione"
                    helpText="Come si risolve il problema. Non serve mettere la formula finale, quella va messa quando inserisci una domanda."
                    value={getIn(values, LintedField.Soluzione) || ''}
                    touched={!!getIn(touched, LintedField.Soluzione)}
                    error={getIn(errors, LintedField.Soluzione)}
                    onChange={(value) => lint(LintedField.Soluzione, values, setFieldValue, value)}
                    onBlur={() => setFieldTouched(LintedField.Soluzione, true, true)}
                  />
                  {!!getIn(values, LintedField.Soluzione) &&
                    <AnteprimaLaTeX fieldname={LintedField.Soluzione} latex_api={api_soluzione} mode="compiled" />
                  }
                  {values.problema.tipologia_risposta === "num" &&
                    <MultipleSoluzioniForm />
                  }
                  {values.problema.tipologia_risposta === "mul" &&
                    <RisposteMultipleForm />
                  }
                </FormSection>
                <FormSection
                  number={4} title="Categorie ed eventi"
                  description="Indica gli argomenti, le competenze e gli eventi per cui proponi il problema."
                >
                  <CategoriaSelector
                    name="problema.categoria"
                    help_text={"Le categorie colorate sono state selezionate."}
                  />

                  <CategoriaEventoSelector
                    name="problema.eventi_proposto"
                  />
                  <hr className="my-3" />
                  <CompetenzaSelector name="problema.competenze" displayName="Competenze necessarie" help_text={"Le competenze colorate sono state selezionate"} />
                </FormSection>
                <FormSection
                  number={5} title="Fonti e note"
                  description="Aggiungi la fonte e le informazioni riservate a organizzatori e correttori."
                >
                  <FormGroupBootstrap
                    name="problema.fonte" type="select" choices={FONTE}
                    help_text="Da dove ti sei ispirato per scrivere questo problema?"
                  />
                  <FormGroupBootstrap
                    name="problema.fonte_verboso" type="text"
                    help_text="Dettagli extra sulla fonte del problema. Obbligatorio se hai selezionato 'Altro'"
                  />
                  <div className="corrector-notes">
                    <TextHelper field={testoRef} fieldname={LintedField.Commenti} />
                    <div className="row align-items-stretch">
                      <div className="col-12 col-lg-6 d-flex flex-column" style={{ minWidth: 0 }}>
                        <div className="card corrector-notes-editor">
                          <div className="card-body">
                            <FormGroupBootstrap name={LintedField.Commenti} type="textarea" ref={testoRef} />
                          </div>
                        </div>
                      </div>
                      <div className="col-12 col-lg-6 d-flex flex-column mt-3 mt-lg-0" style={{ minWidth: 0 }}>
                        <Anteprima fieldname={LintedField.Commenti} />
                      </div>
                    </div>
                    <small className="form-text text-muted">Commenti che non devono finire nel testo del problema, riservati agli organizzatori e correttori</small>
                  </div>
                </FormSection>
                <div className="d-flex flex-wrap justify-content-end">
                  <ConfirmedButton
                    type="warning"
                    onSubmit={handleReset}>
                    Reset
                  </ConfirmedButton>
                  {!modifica &&
                    <button type="button" disabled={isSubmitting}
                      onClick={() => { subButton.current = Button.Bozza; handleSubmit() }}
                      className="btn btn-belize-hole">Salva come bozza</button>
                  }
                  <button type="button" disabled={isSubmitting}
                    onClick={() => { subButton.current = Button.Invia; handleSubmit() }}
                    className="mx-1 btn btn-primary">Invia</button>
                </div>
                <ErrorBlockGeneric error={error} hidejson={may_i_hide_json} />
              </Form>
            </>
          );
        }}
      </Formik>
    </div>
  );
}


export default NuovoThreadProblema;
